// simple-excel-upload.service.ts

import { Injectable } from '@angular/core';
import {
  ExcelUploaderConfig,
  MissingCatalogValue,
  ProcessResult,
  ValidationResult,
  DuplicateValidationResult,
  InternalDuplicate,
  DatabaseDuplicate,
  CatalogMapping,
  ArrayItemColumn
} from '@app/shared/models/simple-excel-upload.model';

@Injectable({
  providedIn: 'root'
})
export class SimpleExcelUploadService {

  /**
   * Cache de `row` → `{normalizedKey: realKey}`. La normalización es costosa
   * (regex + normalize NFD) y se hace una vez por fila, no por celda.
   * Limpiamos el cache al inicio de cada uploadExcelFile.
   */
  private headerKeyCache = new WeakMap<object, Record<string, string>>();

  constructor() { }

  /**
   * Normaliza un header (de Excel o de config) para comparación tolerante.
   * Quita acentos, espacios extra, lo deja en MAYÚSCULAS.
   * Ejemplos:
   *   "Razón Social"     → "RAZON SOCIAL"
   *   "  razon social"   → "RAZON SOCIAL"
   *   "TELÉFONO"         → "TELEFONO"
   */
  private normalizeHeaderKey(key: string): string {
    return key
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toUpperCase();
  }

  /**
   * Lee el valor de una celda buscando el `excelColumn` con comparación
   * normalizada (sin acentos, sin importar mayúsculas/espacios).
   *
   * Si Excel tiene `"razon social"` y la config pide `"Razón Social"`, matchea.
   * Si no encuentra coincidencia, devuelve `undefined`.
   *
   * `aliases` son encabezados viejos de la misma columna. Se prueban sólo
   * si el principal no apareció, y sirven para que una columna renombrada
   * siga leyendo los archivos que la gente ya tenía.
   */
  private getCellValue(row: any, excelColumn: string, aliases?: string[]): any {
    if (!row) return undefined;

    // Construir/leer cache de claves normalizadas para esta fila
    let normalizedMap = this.headerKeyCache.get(row);
    if (!normalizedMap) {
      normalizedMap = {};
      for (const realKey of Object.keys(row)) {
        normalizedMap[this.normalizeHeaderKey(realKey)] = realKey;
      }
      this.headerKeyCache.set(row, normalizedMap);
    }

    for (const nombre of [excelColumn, ...(aliases ?? [])]) {
      // Acceso directo primero (path rápido cuando ya coinciden exacto)
      if (nombre in row) return row[nombre];
      const realKey = normalizedMap[this.normalizeHeaderKey(nombre)];
      if (realKey !== undefined) return row[realKey];
    }

    return undefined;
  }

  async uploadExcelFile(config: ExcelUploaderConfig): Promise<any> {
    try {
      let xlsxData: any[] = await config.services.uploadFileDialog.open();
      const sheets: Record<string, any[]> = (xlsxData as any)?.sheets ?? {};
      if (config.preprocess) xlsxData = config.preprocess(xlsxData, sheets);
      console.log('=== DATOS CRUDOS DEL EXCEL ===');
      console.log('Total filas:', xlsxData.length);
      console.log('Primeras 3 filas:', xlsxData.slice(0, 3));

      const processResult = config.groupedConfig
        ? this.processGroupedExcelData(xlsxData, config)
        : this.processExcelData(xlsxData, config);
      const missingCatalogValues = processResult.missingCatalogValues;
      const processedData = config.postprocess
        ? config.postprocess(processResult.processedData, sheets)
        : processResult.processedData;

      console.log('=== DATOS PROCESADOS ===');
      console.log('Total procesadas:', processedData.length);
      console.log('Primeras 3 filas procesadas:', processedData.slice(0, 3));
      console.log('Valores faltantes en catálogos:', missingCatalogValues);

      if (missingCatalogValues.length > 0) {
        if (config.onMissingCatalogValues) {
          await config.onMissingCatalogValues(missingCatalogValues);
        } else {
          await this.showMissingCatalogValuesMessage(missingCatalogValues, config);
        }
        return null;
      }

      const validationResult = await this.validateRequiredFields(processedData, config);
      if (!validationResult.isValid) return null;

      if (config.validation.validateItemNumbers && config.activeContract) {
        const itemNumberValidation = await this.validateItemNumbers(processedData, config);
        if (!itemNumberValidation.isValid) return null;
      }

      config.services.customMessageService.open({
        title: 'Espere', type: 'success', message: 'Verificando duplicados...', loading: true
      });

      const skipDbCheck = config.skipSave === true;
      const duplicateValidation = await this.checkForDuplicates(processedData, config, skipDbCheck);
      config.services.customMessageService.close();

      console.log('=== VALIDACIÓN DE DUPLICADOS ===');
      console.log('Duplicados internos (Excel):', duplicateValidation.internalDuplicates.length);
      console.log('Duplicados en BD:', duplicateValidation.databaseDuplicates.length);
      console.log('Elementos únicos a crear:', duplicateValidation.uniqueItems.length);
      console.log('Elementos a actualizar:', duplicateValidation.itemsToUpdate.length);

      if (duplicateValidation.internalDuplicates.length > 0) {
        await this.showInternalDuplicatesError(duplicateValidation.internalDuplicates, config);
        return null;
      }

      if (duplicateValidation.itemsToUpdate.length > 0 && config.validation.allowUpdate) {
        await this.showUpdateMessage(duplicateValidation.itemsToUpdate.length, duplicateValidation.uniqueItems.length, config);
      } else if (duplicateValidation.itemsToUpdate.length > 0 && !config.validation.allowUpdate) {
        await this.showDuplicatesNotAllowedMessage(duplicateValidation.itemsToUpdate, config);
        return null;
      }

      if (duplicateValidation.uniqueItems.length === 0 && duplicateValidation.itemsToUpdate.length === 0) {
        await config.services.customMessageService.open({
          title: 'Información', type: 'neutral', message: 'No hay elementos para procesar.', dismissBtn: true
        });
        return null;
      }

      if (config.skipSave) {
        console.log('=== MODO SKIP SAVE: RETORNANDO DATOS SIN GUARDAR ===');
        return {
          uniqueItems: duplicateValidation.uniqueItems,
          itemsToUpdate: duplicateValidation.itemsToUpdate,
          allItems: [...duplicateValidation.uniqueItems, ...duplicateValidation.itemsToUpdate]
        };
      }

      let searchResult = null;
      if (duplicateValidation.uniqueItems.length > 0) {
        searchResult = await this.handleCreate(duplicateValidation.uniqueItems, config);
      }
      if (duplicateValidation.itemsToUpdate.length > 0 && config.validation.allowUpdate) {
        searchResult = await this.handleUpdate(duplicateValidation.itemsToUpdate, config);
      }

      return searchResult;

    } catch (err) {
      console.error('=== ERROR EN UPLOAD EXCEL ===', err);
      config.services.customMessageService.close();
      await config.services.customMessageService.open({
        title: 'Error', type: 'error', message: `Error: ${this.getErrorMessage(err)}`, dismissBtn: true
      });
      return null;
    }
  }

  // ─── Helpers para targetColumn simple o compuesto ────────────────────────────

  /**
   * Construye la clave normalizada de un item.
   * Si targetColumn es array, concatena los valores con '||'.
   */
  private getTargetKey(item: any, targetColumn: string | string[]): string {
    if (Array.isArray(targetColumn)) {
      return targetColumn
        .map(col => this.normalizeText(this.getObjectValue(item, col)))
        .join('||');
    }
    return this.normalizeText(this.getObjectValue(item, targetColumn));
  }

  /**
   * Igual que getTargetKey pero sin normalizar (para guardar el valor original).
   */
  private getTargetKeyOriginal(item: any, targetColumn: string | string[]): string {
    if (Array.isArray(targetColumn)) {
      return targetColumn
        .map(col => String(this.getObjectValue(item, col) ?? ''))
        .join('||');
    }
    return this.getObjectValue(item, targetColumn);
  }

  // ─── Helper para agregar un valor a un array, con o sin catálogo ─────────────

  /**
   * Agrega el valor de arrayColumn al array del grupo.
   * Si viene arrayCatalogMapping, resuelve el código al ID antes de agregar.
   * Resultado: ['uuid-1', 'uuid-2'] en lugar de [{ key: 'uuid-1' }].
   */
  private pushArrayValue(
    row: any,
    rowIndex: number,
    arrayColumn: string,
    arrayObjectKey: string,
    group: any,
    missingCatalogValues: MissingCatalogValue[],
    arrayCatalogMapping?: CatalogMapping
  ): void {
    const rawValue = this.getCellValue(row, arrayColumn);
    if (rawValue === null || rawValue === undefined || rawValue.toString().trim() === '') return;

    const strValue = rawValue.toString().trim();

    if (arrayCatalogMapping) {
      const resolved = this.mapToCatalog(strValue, arrayCatalogMapping);
      if (resolved === null) {
        missingCatalogValues.push({
          row: rowIndex,
          field: arrayObjectKey,
          value: strValue,
          excelColumn: arrayColumn
        });
      } else {
        this.getObjectValue(group, arrayObjectKey).push(resolved);
      }
    } else {
      this.getObjectValue(group, arrayObjectKey).push(strValue);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────

  private normalizeText(text: any): string {
    if (!text) return '';
    return text.toString().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
  }

  private processExcelData(xlsxData: any[], config: ExcelUploaderConfig): ProcessResult {
    const processed = [];
    const missingCatalogValues: MissingCatalogValue[] = [];

    for (let i = 0; i < xlsxData.length; i++) {
      const row = xlsxData[i];
      const processedRow: any = {};
      let skipRow = false;

      // Fila "vacía" que Excel no descarta porque trae espacios (incluidos
      // espacios duros): se ignora en vez de reportarla como incompleta.
      const blank = config.columns.every(c => {
        const v = this.getCellValue(row, c.excelColumn, c.excelColumnAliases);
        return v === null || v === undefined || v.toString().replace(/\s+/g, '').trim() === '';
      });
      if (blank) continue;

      for (const columnConfig of config.columns) {
        let value = this.getCellValue(row, columnConfig.excelColumn, columnConfig.excelColumnAliases);

        if (columnConfig.catalogMapping) {
          if (value && value.toString().trim() !== '') {
            const mappedValue = this.mapToCatalog(value, columnConfig.catalogMapping);
            if (mappedValue === null) {
              missingCatalogValues.push({ row: i + 2, field: columnConfig.objectKey, value: value.toString().trim(), excelColumn: columnConfig.excelColumn });
              skipRow = true;
              break;
            }
            value = mappedValue;
          } else {
            value = null;
          }
        } else {
          value = columnConfig.transform ? columnConfig.transform(value) : this.normalizeText(value);
        }

        this.setObjectValue(processedRow, columnConfig.objectKey, value);
      }

      if (!skipRow) {
        if (config.validation.itemNumberColumn && config.activeContract) {
          const itemNumber = row[config.validation.itemNumberColumn]?.toString();
          if (itemNumber) {
            processedRow.itemNumber = processedRow.itemNumber || {};
            processedRow.itemNumber[config.activeContract.id] = itemNumber;
          }
        }
        processedRow._excelRow = i + 2;
        processed.push(processedRow);
      }
    }

    return { processedData: processed, missingCatalogValues };
  }

  /**
   * Router: delega a modo columna simple o clave compuesta.
   */
  private processGroupedExcelData(xlsxData: any[], config: ExcelUploaderConfig): ProcessResult {
    const gc = config.groupedConfig!;
    if (gc.groupByColumns && gc.groupByColumns.length > 0) {
      return this.processGroupedByCompositeKey(xlsxData, config);
    }
    return this.processGroupedBySingleColumn(xlsxData, config);
  }

  /**
   * Modo original: columna vacía = continuación del grupo anterior.
   * Ej: kits donde Nombre vacío = producto adicional del kit anterior.
   */
  private processGroupedBySingleColumn(xlsxData: any[], config: ExcelUploaderConfig): ProcessResult {
    const processed = [];
    const missingCatalogValues: MissingCatalogValue[] = [];
    const { groupByColumn, arrayColumn, arrayObjectKey, arrayItemColumns, arrayCatalogMapping } = config.groupedConfig!;

    let currentGroup: any = null;

    for (let i = 0; i < xlsxData.length; i++) {
      const row = xlsxData[i];
      const groupValue = row[groupByColumn!];

      if (groupValue !== null && groupValue !== undefined && groupValue.toString().trim() !== '') {
        if (currentGroup) processed.push(currentGroup);

        const newGroup: any = {};
        let skipGroup = false;

        for (const columnConfig of config.columns) {
          if (arrayColumn && columnConfig.excelColumn === arrayColumn) continue;
          if (arrayItemColumns && arrayItemColumns.some(c => c.excelColumn === columnConfig.excelColumn)) continue;

          let value = this.getCellValue(row, columnConfig.excelColumn, columnConfig.excelColumnAliases);

          if (columnConfig.catalogMapping) {
            if (value && value.toString().trim() !== '') {
              const mappedValue = this.mapToCatalog(value, columnConfig.catalogMapping);
              if (mappedValue === null) {
                missingCatalogValues.push({ row: i + 2, field: columnConfig.objectKey, value: value.toString().trim(), excelColumn: columnConfig.excelColumn });
                skipGroup = true;
                break;
              }
              value = mappedValue;
            } else {
              value = null;
            }
          } else {
            value = columnConfig.transform ? columnConfig.transform(value) : this.normalizeText(value);
          }

          this.setObjectValue(newGroup, columnConfig.objectKey, value);
        }

        if (!skipGroup) {
          this.setObjectValue(newGroup, arrayObjectKey, []);

          if (arrayItemColumns && arrayItemColumns.length > 0) {
            const arrayItem = this.createArrayItem(row, arrayItemColumns, i + 2, missingCatalogValues);
            if (arrayItem !== null) {
              this.getObjectValue(newGroup, arrayObjectKey).push(arrayItem);
            } else {
              skipGroup = true;
            }
          } else if (arrayColumn) {
            this.pushArrayValue(row, i + 2, arrayColumn, arrayObjectKey, newGroup, missingCatalogValues, arrayCatalogMapping);
          }

          if (!skipGroup) {
            newGroup._excelRow = i + 2;
            currentGroup = newGroup;
          } else {
            currentGroup = null;
          }
        } else {
          currentGroup = null;
        }

      } else if (currentGroup) {
        if (arrayItemColumns && arrayItemColumns.length > 0) {
          const arrayItem = this.createArrayItem(row, arrayItemColumns, i + 2, missingCatalogValues);
          if (arrayItem !== null) {
            const arr = this.getObjectValue(currentGroup, arrayObjectKey);
            if (arr) arr.push(arrayItem);
          }
        } else if (arrayColumn) {
          this.pushArrayValue(row, i + 2, arrayColumn, arrayObjectKey, currentGroup, missingCatalogValues, arrayCatalogMapping);
        }
      }
    }

    if (currentGroup) processed.push(currentGroup);

    console.log(`=== DATOS AGRUPADOS (single column): ${processed.length} grupos ===`);
    return { processedData: processed, missingCatalogValues };
  }

  /**
   * Modo clave compuesta: agrupa por igualdad de groupByColumns.
   * Cada combinación única = un registro. Cada fila acumula items en el array.
   *
   * | Contrato | Bloque | Partida | Código Producto |
   * | C001     | B1     | P1      | PROD-A          |  ← grupo C001-B1-P1
   * | C001     | B1     | P1      | PROD-B          |  ← mismo grupo
   * | C001     | B1     | P2      | PROD-C          |  ← grupo nuevo
   */
  private processGroupedByCompositeKey(xlsxData: any[], config: ExcelUploaderConfig): ProcessResult {
    const processed: any[] = [];
    const missingCatalogValues: MissingCatalogValue[] = [];
    const { groupByColumns, arrayColumn, arrayObjectKey, arrayItemColumns, arrayCatalogMapping } = config.groupedConfig!;

    const groupMap = new Map<string, number>(); // clave compuesta → índice en processed[]

    for (let i = 0; i < xlsxData.length; i++) {
      const row = xlsxData[i];

      // Encabezado tolerante (acentos/espacios) y valor normalizado: en los
      // archivos reales el mismo nombre llega con y sin espacios duros.
      const keyParts = groupByColumns!.map(col => {
        const v = this.getCellValue(row, col);
        return v !== null && v !== undefined ? this.normalizeText(v.toString().replace(/\s+/g, ' ')) : '';
      });
      const compositeKey = keyParts.join('||');

      if (keyParts.every(p => p === '')) continue; // fila completamente vacía → saltar

      let groupIndex = groupMap.get(compositeKey);
      let currentGroup: any;

      if (groupIndex === undefined) {
        // Nuevo grupo: procesar columnas base (excluir las del array)
        currentGroup = {};
        let skipGroup = false;

        for (const columnConfig of config.columns) {
          const isArrayCol =
            (arrayColumn && columnConfig.excelColumn === arrayColumn) ||
            (arrayItemColumns && arrayItemColumns.some(c => c.excelColumn === columnConfig.excelColumn));
          if (isArrayCol) continue;

          let value = this.getCellValue(row, columnConfig.excelColumn, columnConfig.excelColumnAliases);

          if (columnConfig.catalogMapping) {
            if (value && value.toString().trim() !== '') {
              const mappedValue = this.mapToCatalog(value, columnConfig.catalogMapping);
              if (mappedValue === null) {
                missingCatalogValues.push({ row: i + 2, field: columnConfig.objectKey, value: value.toString().trim(), excelColumn: columnConfig.excelColumn });
                skipGroup = true;
                break;
              }
              value = mappedValue;
            } else {
              value = null;
            }
          } else {
            value = columnConfig.transform ? columnConfig.transform(value) : this.normalizeText(value);
          }

          this.setObjectValue(currentGroup, columnConfig.objectKey, value);
        }

        if (skipGroup) continue;

        this.setObjectValue(currentGroup, arrayObjectKey, []);
        currentGroup._excelRow = i + 2;

        groupIndex = processed.length;
        processed.push(currentGroup);
        groupMap.set(compositeKey, groupIndex);
      } else {
        currentGroup = processed[groupIndex];
      }

      // Agregar item al array del grupo
      if (arrayItemColumns && arrayItemColumns.length > 0) {
        const arrayItem = this.createArrayItem(row, arrayItemColumns, i + 2, missingCatalogValues);
        if (arrayItem !== null) {
          this.getObjectValue(currentGroup, arrayObjectKey).push(arrayItem);
        }
      } else if (arrayColumn) {
        this.pushArrayValue(row, i + 2, arrayColumn, arrayObjectKey, currentGroup, missingCatalogValues, arrayCatalogMapping);
      }
    }

    console.log(`=== DATOS AGRUPADOS (composite key [${groupByColumns!.join(', ')}]): ${processed.length} grupos ===`);
    processed.slice(0, 3).forEach(g => {
      const arr = this.getObjectValue(g, arrayObjectKey);
      console.log(`  key → ${arr?.length || 0} items`, g);
    });

    return { processedData: processed, missingCatalogValues };
  }

  private createArrayItem(
    row: any,
    arrayItemColumns: ArrayItemColumn[],
    rowNumber: number,
    missingCatalogValues: MissingCatalogValue[]
  ): any | null {
    const arrayItem: any = {};
    let skipItem = false;
    let hasAnyValue = false;

    for (const columnConfig of arrayItemColumns) {
      let value = this.getCellValue(row, columnConfig.excelColumn, columnConfig.excelColumnAliases);
      const hasValue = value !== null && value !== undefined && value.toString().trim() !== '';
      if (hasValue) hasAnyValue = true;

      if (columnConfig.catalogMapping) {
        if (hasValue) {
          const mappedValue = this.mapToCatalog(value, columnConfig.catalogMapping);
          if (mappedValue === null) {
            missingCatalogValues.push({ row: rowNumber, field: columnConfig.objectKey, value: value.toString().trim(), excelColumn: columnConfig.excelColumn });
            skipItem = true;
            break;
          }
          value = mappedValue;
        } else {
          value = null;
        }
      } else {
        if (columnConfig.transform && hasValue) value = columnConfig.transform(value);
        else if (hasValue) value = value.toString().trim();
        else value = null;
      }

      arrayItem[columnConfig.objectKey] = value;
    }

    if (!hasAnyValue || skipItem) return null;
    return arrayItem;
  }

  private async validateRequiredFields(data: any[], config: ExcelUploaderConfig): Promise<ValidationResult> {
    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      for (const columnConfig of config.columns) {
        if (config.groupedConfig) {
          if (config.groupedConfig.arrayColumn && columnConfig.excelColumn === config.groupedConfig.arrayColumn) continue;
          if (config.groupedConfig.arrayItemColumns && config.groupedConfig.arrayItemColumns.some(c => c.excelColumn === columnConfig.excelColumn)) continue;
        }
        if (columnConfig.required) {
          const value = this.getObjectValue(row, columnConfig.objectKey);
          if (!value && value !== false && value !== 0) {
            await config.services.customMessageService.open({
              title: 'Cuidado', type: 'warning',
              message: `El campo '${columnConfig.excelColumn}' es obligatorio.<br>Revisar fila ${row._excelRow || i + 2}`,
              dismissBtn: true
            });
            return { isValid: false };
          }
        }
      }
    }
    return { isValid: true };
  }

  private async validateItemNumbers(data: any[], config: ExcelUploaderConfig): Promise<ValidationResult> {
    const itemNumberList = config.activeContract.products?.map((p: any) => p.itemNumber) || [];
    for (const row of data) {
      const itemNumber = row.itemNumber?.[config.activeContract.id];
      if (itemNumber && !itemNumberList.includes(itemNumber)) {
        await config.services.customMessageService.open({
          title: 'Información', type: 'neutral',
          message: `La partida ${itemNumber} no existe en el contrato activo.<br>Revisa las partidas de tu documento en la fila ${row._excelRow}`,
          dismissBtn: true
        });
        return { isValid: false };
      }
    }
    return { isValid: true };
  }

  private async checkForDuplicates(
    data: any[],
    config: ExcelUploaderConfig,
    skipDatabaseCheck: boolean = false
  ): Promise<DuplicateValidationResult> {
    const internalDuplicates: InternalDuplicate[] = [];
    const databaseDuplicates: DatabaseDuplicate[] = [];
    const uniqueItems: any[] = [];
    const itemsToUpdate: any[] = [];
    const tc = config.validation.targetColumn;

    // 1. Detectar duplicados internos del Excel
    const seenCodes     = new Map<string, number>();
    const seenOriginals = new Map<string, any>();

    for (const item of data) {
      const key = this.getTargetKey(item, tc);
      if (seenCodes.has(key)) {
        internalDuplicates.push({ row: item._excelRow, code: key, firstOccurrence: seenCodes.get(key)! });
      } else {
        seenCodes.set(key, item._excelRow);
        seenOriginals.set(key, item);
      }
    }

    // 2. Buscar en BD (pre-filtro por campo; match exacto en memoria)
    const dbRecordsMap = new Map<string, any>();

    if (!skipDatabaseCheck && config.services.dataService) {
      try {
        let searchCondition: any;

        if (Array.isArray(tc)) {
          searchCondition = tc.reduce((acc, col) => {
            const values = [...seenOriginals.values()]
              .map(item => this.getObjectValue(item, col))
              .filter(v => v != null);
            acc[col] = [...new Set(values)];
            return acc;
          }, {} as Record<string, any[]>);
        } else {
          const uniqueValues = [...seenOriginals.values()]
            .map(item => this.getObjectValue(item, tc));
          searchCondition = { [tc]: [...new Set(uniqueValues)] };
        }

        const dbResponse = await config.services.dataService.search({
          condition: { ...searchCondition, ...config.search },
          searchType: 'default'
        });

        if (dbResponse.state === 'success' && dbResponse.data?.length > 0) {
          dbResponse.data.forEach((dbItem: any) => {
            const dbKey = this.getTargetKey(dbItem, tc);
            dbRecordsMap.set(dbKey, dbItem);
          });
        }
      } catch (error) {
        console.error('Error al buscar duplicados en BD:', error);
      }
    }

    // 3. Clasificar: CREATE vs UPDATE
    for (const item of data) {
      const key = this.getTargetKey(item, tc);
      if (seenCodes.get(key) !== item._excelRow) continue;

      const cleanItem = { ...item };
      delete cleanItem._excelRow;

      if (dbRecordsMap.has(key)) {
        cleanItem.id = dbRecordsMap.get(key).id;
        itemsToUpdate.push(cleanItem);
        databaseDuplicates.push({ row: item._excelRow, code: key });
      } else {
        uniqueItems.push(cleanItem);
      }
    }

    return { internalDuplicates, databaseDuplicates, uniqueItems, itemsToUpdate };
  }

  private async handleCreate(toCreate: any[], config: ExcelUploaderConfig): Promise<any> {
    if (!config.services.dataService) {
      await config.services.customMessageService.open({ title: 'Error de configuración', type: 'error', message: 'No se puede guardar sin un servicio de datos configurado', dismissBtn: true });
      return null;
    }
    const response = await config.services.customMessageService.open({
      title: 'Información', type: 'neutral',
      message: `Se van a crear ${toCreate.length} ${config.entityName} nuevos en el sistema.`,
      dismissBtn: true, acceptBtn: true, acceptBtnText: 'Continuar'
    });
    if (!response) return null;

    config.services.customMessageService.open({ title: 'Espere', type: 'success', message: `Guardando ${config.entityName}`, loading: true });
    try {
      const apiResponse = await config.services.dataService.create({ operationType: 'multiple', records: toCreate });
      config.services.customMessageService.close();
      if (apiResponse.state === 'success') {
        await config.services.customMessageService.open({ title: '¡Hecho!', type: 'success', message: apiResponse.message, duration: 15000 });
        return await this.refreshData(config);
      } else {
        await config.services.customMessageService.open({ title: 'Información', type: 'neutral', message: apiResponse.message, dismissBtn: true });
        return null;
      }
    } catch (serviceError) {
      config.services.customMessageService.close();
      await config.services.customMessageService.open({ title: 'Error', type: 'error', message: `Error al crear elementos: ${this.getErrorMessage(serviceError)}`, dismissBtn: true });
      return null;
    }
  }

  private async handleUpdate(toUpdate: any[], config: ExcelUploaderConfig): Promise<any> {
    if (!config.services.dataService) {
      await config.services.customMessageService.open({ title: 'Error de configuración', type: 'error', message: 'No se puede actualizar sin un servicio de datos configurado', dismissBtn: true });
      return null;
    }
    const response = await config.services.customMessageService.open({
      title: 'Cuidado', type: 'warning',
      message: `Se van a actualizar ${toUpdate.length} ${config.entityName} existentes en el sistema con los datos del Excel.`,
      dismissBtn: true, acceptBtn: true, acceptBtnText: 'Actualizar'
    });
    if (!response) return null;

    config.services.customMessageService.open({ title: 'Espere', type: 'success', message: `Actualizando ${config.entityName}`, loading: true });
    try {
      const apiResponse = await config.services.dataService.update({ operationType: 'multiple', records: toUpdate });
      config.services.customMessageService.close();
      if (apiResponse.state === 'success') {
        await config.services.customMessageService.open({ title: '¡Hecho!', type: 'success', message: apiResponse.message, duration: 15000 });
        return await this.refreshData(config);
      } else {
        await config.services.customMessageService.open({ title: 'Información', type: 'neutral', message: apiResponse.message, dismissBtn: true });
        return null;
      }
    } catch (serviceError) {
      config.services.customMessageService.close();
      await config.services.customMessageService.open({ title: 'Error', type: 'error', message: `Error al actualizar elementos: ${this.getErrorMessage(serviceError)}`, dismissBtn: true });
      return null;
    }
  }

  private async refreshData(config: ExcelUploaderConfig): Promise<any> {
    if (!config.services.dataService) return null;
    try {
      const searchResponse = await config.services.dataService.search({ condition: { ...config.search }, searchType: 'default' });
      return searchResponse.data;
    } catch (searchError) {
      console.error('Error al recargar datos:', searchError);
      if (this.getErrorStatus(searchError) === 401) {
        await config.services.customMessageService.open({ title: 'Sesión Expirada', type: 'warning', message: 'La operación se completó correctamente, pero tu sesión ha expirado. Por favor, actualiza la página e inicia sesión nuevamente.', dismissBtn: true });
      } else {
        await config.services.customMessageService.open({ title: 'Información', type: 'neutral', message: 'La operación se completó correctamente. Por favor, actualiza la página para ver los cambios.', dismissBtn: true });
      }
      return null;
    }
  }

  private async showInternalDuplicatesError(internalDuplicates: InternalDuplicate[], config: ExcelUploaderConfig): Promise<void> {
    let message = `<strong>El archivo Excel contiene valores duplicados (${internalDuplicates.length}):</strong><br><br>`;
    internalDuplicates.slice(0, 10).forEach(item => { message += `• Fila ${item.row}: <strong>${item.code}</strong> (ya apareció en fila ${item.firstOccurrence})<br>`; });
    if (internalDuplicates.length > 10) message += `... y ${internalDuplicates.length - 10} más<br>`;
    message += '<br>Por favor, elimina los duplicados del Excel y vuelve a cargar el archivo.';
    await config.services.customMessageService.open({ title: 'Duplicados en Excel', type: 'error', message, dismissBtn: true });
  }

  private async showUpdateMessage(updateCount: number, createCount: number, config: ExcelUploaderConfig): Promise<void> {
    let message = '';
    if (createCount > 0) message += `<strong style="color: green;">✓ Se crearán ${createCount} elementos nuevos</strong><br>`;
    if (updateCount > 0) message += `<strong style="color: orange;">⚠ Se actualizarán ${updateCount} elementos existentes</strong><br>`;
    await config.services.customMessageService.open({ title: 'Resumen de operaciones', type: 'neutral', message, duration: 5000 });
  }

  private async showDuplicatesNotAllowedMessage(duplicates: any[], config: ExcelUploaderConfig): Promise<void> {
    let message = `<strong>Se encontraron ${duplicates.length} elementos que ya existen en el sistema:</strong><br><br>`;
    duplicates.slice(0, 10).forEach(item => {
      const code = this.getTargetKey(item, config.validation.targetColumn);
      message += `• <strong>${code}</strong><br>`;
    });
    if (duplicates.length > 10) message += `... y ${duplicates.length - 10} más<br>`;
    message += '<br>No se creó ni actualizó ningún elemento. Por favor, elimina los duplicados del Excel.';
    await config.services.customMessageService.open({ title: 'Elementos duplicados', type: 'warning', message, dismissBtn: true });
  }

  private async showMissingCatalogValuesMessage(missingValues: MissingCatalogValue[], config: ExcelUploaderConfig): Promise<void> {
    const groupedByField = missingValues.reduce((acc, item) => {
      if (!acc[item.field]) acc[item.field] = [];
      acc[item.field].push(item);
      return acc;
    }, {} as Record<string, MissingCatalogValue[]>);

    let message = 'Se encontraron valores que no existen en los catálogos:<br><br>';
    Object.keys(groupedByField).forEach(field => {
      const items = groupedByField[field];
      message += `<strong>${items[0].excelColumn}:</strong><br>`;
      items.slice(0, 5).forEach(item => { message += `• Fila ${item.row}: "${item.value}"<br>`; });
      if (items.length > 5) message += `... y ${items.length - 5} más<br>`;
      message += '<br>';
    });
    message += `Se omitieron ${missingValues.length} filas. Por favor, corrige estos valores y vuelve a cargar el archivo.`;
    await config.services.customMessageService.open({ title: 'Valores no encontrados en catálogos', type: 'warning', message, dismissBtn: true });
  }

  private mapToCatalog(value: string, mapping: CatalogMapping): any {
    if (!value || !mapping.catalog || mapping.catalog.length === 0) return null;
    // Normaliza para el cotejo: respeta `caseSensitive` y, además, colapsa
    // espacios internos repetidos. Sin esto, valores correctos pero con doble
    // espacio o tabs (frecuentes al copiar a Excel) fallaban el match y el
    // sistema reportaba que el nombre "no existe" aunque sí está en el catálogo.
    const norm = (v: any): string => {
      const base = mapping.caseSensitive ? v.toString().trim() : this.normalizeText(v);
      return base.replace(/\s+/g, ' ');
    };
    const searchValue = norm(value);
    const foundItem = mapping.catalog.find(item => {
      if (!item || item[mapping.nameField] === undefined || item[mapping.nameField] === null) return false;
      return norm(item[mapping.nameField]) === searchValue;
    });
    if (!foundItem) console.warn(`✗ No se encontró "${searchValue}" en el catálogo.`);
    return foundItem ? foundItem[mapping.idField] : null;
  }

  private getObjectValue(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }

  private setObjectValue(obj: any, path: string, value: any): void {
    const keys = path.split('.');
    const lastKey = keys.pop()!;
    const target = keys.reduce((current, key) => {
      if (!current[key]) current[key] = {};
      return current[key];
    }, obj);
    target[lastKey] = value;
  }

  // ─── Helpers para errores tipados como `unknown` ─────────────────────────
  //
  // En strict mode TS, `catch (err)` da `err: unknown`, así que no se
  // puede acceder directo a `err.message` o `err.status`. Estos helpers
  // hacen el narrowing seguro y devuelven defaults si no se puede.

  private getErrorMessage(err: unknown): string {
    if (err instanceof Error) return err.message;
    if (typeof err === 'object' && err !== null && 'message' in err) {
      return String((err as { message: unknown }).message);
    }
    return String(err);
  }

  private getErrorStatus(err: unknown): number | undefined {
    if (typeof err === 'object' && err !== null && 'status' in err) {
      const status = (err as { status: unknown }).status;
      return typeof status === 'number' ? status : undefined;
    }
    return undefined;
  }
}